<template>
  <div class="chart-container">
    <svg
      ref="chart"
      class="chart-content"
      :height="elementHeight + 'px'"
      width="100%"
      @mousemove="onMouseMove"
      @mouseleave="onMouseLeave"
      @touchmove="onMouseMove"
    >
      <defs>
        <pattern
          v-for="line in sortedLines"
          v-bind:key="line.id"
          v-bind:id="line.id + 'point'"
          :width="line.parties.length * 4"
          :height="line.parties.length * 4"
          patternTransform="rotate(60 0 0)"
          patternUnits="userSpaceOnUse"
        >
          <rect
            x="0"
            y="0"
            :width="line.parties.length * 4"
            :height="line.parties.length * 4"
            fill="white"
          />
          <line
            v-for="(party, i) in line.parties"
            v-bind:key="i"
            :x1="2 + i * 4"
            y1="0"
            :x2="2 + i * 4"
            :y2="line.parties.length * 4"
            :class="['stroke-party-' + party]"
            :style="{ 'stroke-width': 4 }"
          />
        </pattern>
      </defs>
      <g :transform="'translate(' + margin.left + ', ' + margin.top + ')'">
        <g
          v-for="(tick, i) in yTicks"
          :key="i"
          class="tick"
          :transform="'translate(0, ' + tick.y + ')'"
        >
          <line :x2="width" class="grid" />
        </g>
      </g>
      <g :transform="'translate(' + margin.left + ', ' + margin.top + ')'">
        <g
          v-for="milestone in milestones"
          :key="milestone.date"
          :transform="'translate(' + xScale(parseTime(milestone.date)) + ', 0)'"
        >
          <text class="label milestone" y="15" transform="rotate(90)">
            {{ milestone.title }}
          </text>
          <line :y2="height" class="milestone" />
        </g>
      </g>
      <g
        v-for="line in sortedLines"
        :key="line.id"
        :transform="'translate(' + margin.left + ', ' + margin.top + ')'"
        class="estimate-group"
        :class="{
          disabled: highlightLine !== null && highlightLine != line.id,
        }"
      >
        <path class="estimate" :class="line.color == 'party' ? 'stroke-party-' + line.parties[0] : 'stroke-neutral'" :d="genPath(line.points)" />
      </g>
      <g
        :transform="'translate(' + margin.left + ', ' + margin.top + ')'"
        text-anchor="end"
      >
        <g
          v-for="(tick, i) in yTicks"
          :key="i"
          class="tick"
          :transform="'translate(0, ' + tick.y + ')'"
        >
          <text class="label" x="-9" dy="0.32em">{{ tick.text }}</text>
        </g>
      </g>
      <g
        :transform="
          'translate(' + margin.left + ', ' + (margin.top + height) + ')'
        "
        text-anchor="middle"
      >
        <g
          v-for="(tick, i) in xTicks"
          :key="i"
          class="tick"
          :transform="'translate(' + tick.x + ', 0)'"
        >
          <text class="label" y="9" dy="0.71em">{{ tick.text }}</text>
          <line class="grid" y2="6" stroke-width="0.5"></line>
        </g>
      </g>
      <g :transform="'translate(' + margin.left + ', ' + margin.top + ')'">
        <g :transform="'translate(' + xScale(parseTime(activeDate)) + ', 0)'">
          <line :y2="height" class="grid" stroke-dasharray="5,5" />
        </g>
      </g>
      <g :transform="'translate(' + margin.left + ', ' + margin.top + ')'">
        <g :transform="'translate(' + xScale(parseTime(activeDate)) + ',0)'">
          <text text-anchor="middle" y="-10">
            {{ formatTime(parseTime(activeDate)) }}
          </text>
          <rect
            :width="width - xScale(parseTime(activeDate)) + 10"
            class="hover-overlay"
            :height="height"
          />
          <g
            v-for="note in sortedAnnotations"
            :key="note.id"
            text-anchor="left"
            class="annotation"
            :class="{disabled: highlightLine !== null && highlightLine != note.id, neutral: note.color == 'neutral'}"
          >
            <circle
              r="10"
              :cy="note.y"
              :fill="'url(#' + note.id + 'point)'"
              class="point"
              :style="{opacity: note.color == 'party' ? '0.5' : ''}"
            />
            <circle
              v-if="note.color == 'party'"
              r="5"
              :cy="note.y"
              :fill="'url(#' + note.id + 'point)'"
              class="point"
            />
            <text
              class="note"
              dominant-baseline="middle"
              x="15"
              :y="note.y"
              :class="{active: note.id == highlightLine}"
              v-if="highlightLine == note.id"
            >
              {{ mobile ? "" : note.text + ": " }}{{ note.value | notnull }}
            </text>
          </g>
        </g>
      </g>
    </svg>
    <div class="legend-container">
      <div class="legend">
        <div v-for="item in legendItems" :key="item.id" class="legend-item" @mouseover="highlightLine = item.id">
          <svg height="20px" width="20px">
            <defs>
              <pattern
                :id="'striped-' + item.id"
                :width="item.parties.length * 4"
                :height="item.parties.length * 4"
                patternTransform="rotate(60 0 0)"
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x="0"
                  y="0"
                  :width="item.parties.length * 4"
                  :height="item.parties.length * 4"
                  fill="white"
                />
                <line
                  v-for="(party, i) in item.parties"
                  v-bind:key="i"
                  :x1="2 + i * 4"
                  y1="0"
                  :x2="2 + i * 4"
                  :y2="item.parties.length * 4"
                  :class="['stroke-party-' + party]"
                  :style="{ 'stroke-width': 4 }"
                />
              </pattern>
            </defs>
            <circle
              cx="10"
              cy="10"
              r="10"
              :fill="'url(#striped-' + item.id + ')'"
            />
          </svg>
          <span :class="{active: item.id == highlightLine}">
            {{ item.title }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from "d3";
import { max, min, flatten, map, orderBy } from "lodash";
import { mapState } from "vuex";

export default {
  name: "LineChart",
  props: ["data", "elementHeight", "dateFormat"],
  data() {
    return {
      domYAxis: null,
      svg: null,
      legend: null,
      parseTime: d3.isoParse,
      annotationAt: null,
      highlightLine: null,
      hoverDate: "2021-11-09",
      clientWidth: 600,
      milestones: [
        { date: "2021-02-03T00:00:00.000Z", title: this.$t("other_update_model")},
        { date: "2021-05-27T00:00:00.000Z", title: this.$t("other_update_model")},
      ],
    };
  },
  computed: {
    ...mapState(["windowWidth"]),
    mobile() {
      return this.windowWidth < 800;
    },
    margin() {
      if (this.mobile) {
        return { top: 30, right: 50, bottom: 35, left: 40 };
      } else {
        return { top: 30, right: 220, bottom: 35, left: 40 };
      }
    },
    maxTime() {
      return max(
        flatten(map(this.data.lines, (l) => map(l.points, "x")))
      );
    },
    minTime() {
      return min(
        flatten(map(this.data.lines, (l) => map(l.points, "x")))
      );
    },
    maxY() {
      return max(
        flatten(map(this.data.lines, (l) => map(l.points, "y")))
      );
    },
    width() {
      return this.clientWidth - this.margin.left - this.margin.right;
    },
    height() {
      return this.elementHeight - this.margin.top - this.margin.bottom;
    },
    yScale() {
      return d3
        .scaleLinear()
        .domain([0, this.maxY + 0.05])
        .range([this.height, 0]);
    },
    xScale() {
      return d3
        .scaleTime()
        .domain([this.parseTime(this.minTime), this.parseTime(this.maxTime)])
        .range([0, this.width]);
    },
    yTicks() {
      let y = this.yScale.ticks(5);

      return map(y, (o) => ({ y: this.yScale(o), text: o * 100 + " %" }));
    },
    xTicks() {
      let nTicks = this.width / 100;
      let x = this.xScale.ticks(nTicks);
      let format = this.xScale.tickFormat(nTicks, this.dateFormat);

      return map(x, (o) => ({ x: this.xScale(o), text: format(o) }));
    },
    annotations() {
      let formatDate = d3.timeFormat("%d.%m.%Y");

      let notes = map(this.data.lines, (o) => {
        let dates = map(o.points, "x");
        let index = dates.indexOf(this.activeDate);


        if (!o.points[index]) return {}

        return {
          id: o.id,
          text: o.title,
          color: o.color,
          value: Math.round(o.points[index].y * 100) + " %",
          date: formatDate(this.parseTime(o.points[index].x)),
          x: this.xScale(this.parseTime(o.points[index].x)),
          y: this.yScale(o.points[index].y),
        };
      });

      return notes;
    },
    activeDate() {
      if (this.data.lines.length == 0) return this.hoverDate;

      let dates = map(this.data.lines[0].points, "x");

      let dist = map(dates, (o) =>
        Math.abs(this.parseTime(o) - this.parseTime(this.hoverDate))
      );
      let mindist = min(dist);
      let index = dist.indexOf(mindist);

      return dates[index];
    },
    legendItems () {
      return orderBy(this.data.lines, o => o.points[o.points.length - 1].y, ["desc"]);
    },
    sortedLines () {
      return orderBy(this.data.lines, o => o.id == this.highlightLine);
    },
    sortedAnnotations () {
      return orderBy(this.annotations, o => o.id == this.highlightLine);
    },
    formatTime () {
      return d3.timeFormat(this.$t("format_date_long"))
    },
  },
  methods: {
    genPath(points) {
      let line = d3
        .line()
        .x((d) => this.xScale(this.parseTime(d.x)))
        .y((d) => this.yScale(d.estimate));

      return line(points);
    },
    onMouseMove(event) {
      if (window.TouchEvent && event instanceof TouchEvent) event = event.touches[0];
      let pointer = d3.pointer(event, this.$refs.chart);

      let coords = [
        pointer[0] - this.margin.left,
        pointer[1] - this.margin.top,
      ];

      if (
        coords[0] < 0 ||
        coords[0] > this.width + this.margin.right ||
        coords[1] < 0 ||
        coords[1] > this.height
      ) {
        this.hoverDate = "2021-09-26";
        this.highlightLine = null;
        return;
      }

      if (this.moveTimeout == null) {
        this.moveTimeout = setTimeout(() => {
          this.hoverDate = d3.timeFormat("%Y-%m-%d")(
            this.xScale.invert(coords[0])
          );
          this.highlight(this.yScale.invert(coords[1]), coords[0] > this.width);
          this.moveTimeout = null;
        }, 10);
      }
    },
    highlight(estimate) {
        let y = map(this.annotations, o => Math.abs(o.y - this.yScale(estimate)));
        let index = y.indexOf(min(y));

        this.highlightLine = this.annotations[index].id;
    },
    onMouseLeave() {
      this.hoverDate = "2021-09-26";
      this.highlightLine = null;
    },
    handleResize() {
      if (this.$refs.chart) {
        this.clientWidth = this.$refs.chart.clientWidth;
      }
    },
    diff(x, y) {
      return Math.abs(y - x);
    },
  },
  mounted() {
    this.handleResize();
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  filters: {
    notnull (val) {
      if (val == '0 %') return '< 1 %';
      else return val;
    }
  },
  watch: {
    highlightLine (val) {
      this.$emit('hover', val);
    }
  }
};
</script>

<style scoped>
.legend-container {
  max-width: 700px;
}
.estimate.stroke-neutral {
  stroke: var(--blue-dark);
  stroke-width: 2;
  fill: none;
}

.estimate-group.disabled .estimate.stroke-neutral {
  /* stroke: var(--blue-primary); */
  opacity: 0.2;
}

.annotation.neutral.disabled  circle {
  opacity: 0.2;
}

.legend-item span.active {
  font-style: italic;
}

svg {
  touch-action: pan-y;
}
</style>